import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { VariableSizeList } from 'react-window';

import { PhotoShootItemType } from 'apis/mips/types';
import { localize } from 'common/utils';

import { useListItemsCollapsibles } from './useListItemsCollapsibles';
import { useListItemsSearch } from './useListItemsSearch';
import usePhotoShootContext from '../../../hooks/usePhotoShootContext';
import { useUnlinkMutation } from '../../../hooks/useUnlinkMutation';
import { PhotoShootHeaderItem, PhotoShootListItem, PhotoShootMenuItem } from '../../../types';
import { CoverItem, ListRowMenuItemProps, VirtualizedListRowType } from '../types';

export type UseListItemsArgs = {
  menuItems: PhotoShootMenuItem[];
  headerItems: PhotoShootHeaderItem[];
  listItems: PhotoShootListItem[];
  isReadOnly: boolean;
};

export const useLinkItems = ({ menuItems, headerItems, listItems, isReadOnly }: UseListItemsArgs) => {
  const listRef = useRef<VariableSizeList | null>(null);
  const { venueId, token } = usePhotoShootContext();
  const { collapsedItems, handleCollapse } = useListItemsCollapsibles();
  const { searchValue, handleSearch } = useListItemsSearch();
  const {
    mutation: { mutate: handleUnlink },
    loadingItems,
  } = useUnlinkMutation({ venueId, token });
  const intl = useIntl();

  const linksListItems = useMemo<ListRowMenuItemProps[]>(() => {
    const lowerCasedSearchValue = searchValue.toLowerCase();

    return menuItems.reduce((acc, item, currentIndex) => {
      const lowerCasedItemName = localize(item.name, intl.locale).toLowerCase();

      if (!lowerCasedItemName.includes(lowerCasedSearchValue)) {
        return acc;
      }

      const isCollapsed = collapsedItems.has(item.id);
      const shouldBeCollapsed = item.images.length > 1;
      if (!isCollapsed && shouldBeCollapsed) {
        handleCollapse(item.id);
      }

      return [
        ...acc,
        {
          ...item,
          type: VirtualizedListRowType.MENU_ITEM,
          onUnlink: handleUnlink,
          onCollapse: () => {
            handleCollapse(item.id);
            listRef.current?.resetAfterIndex(currentIndex);
          },
          isCollapsed,
          isLoading: loadingItems.has(item.id),
          isReadOnly,
        },
      ];
    }, []);
  }, [searchValue, menuItems, intl.locale, collapsedItems, handleUnlink, loadingItems, isReadOnly, handleCollapse]);

  const { linkHeaderItems, linkListItems } = useMemo<{
    linkHeaderItems: CoverItem[];
    linkListItems: CoverItem[];
  }>(
    () =>
      [...listItems, ...headerItems].reduce(
        (acc, item) => {
          const newItem = {
            ...item,
            onUnlink: handleUnlink,
            isLoading: loadingItems.has(item.id),
            isReadOnly,
          };

          if (item.itemType === PhotoShootItemType.LIST) {
            return {
              ...acc,
              linkListItems: [...acc.linkListItems, newItem],
            };
          }

          return {
            ...acc,
            linkHeaderItems: [...acc.linkHeaderItems, newItem],
          };
        },
        {
          linkHeaderItems: [],
          linkListItems: [],
        },
      ),
    [headerItems, listItems, handleUnlink, isReadOnly, loadingItems],
  );

  return {
    linkMenuItems: linksListItems,
    linkHeaderItems,
    linkListItems,
    listRef,
    handleSearch,
  };
};
